// ---- Bird archetype ----
// The shared avian anatomy (egg body, folded wings, tapered tail, thin legs,
// raised head + beak), parameterised by proportions + palette — so a chicken /
// crow / duck is a palette + proportions + a details part-list (comb, wattle,
// eyes) instead of a from-scratch build. Rounded build (plan 28 stage 2): same
// option surface, limb pivots, and anchor formulas as the original boxy build.
// Same contract as quadruped/humanoid: feet at y=0, facing +z, pure meshkit
// prims, returns named anchor points.

import type { Prims } from "../meshkit.ts";
import { shade } from "../color.ts";
import { ARCHETYPE_ANCHORS, type Vec3 } from "./parts.ts";

/** Proportions + palette for the shared bird body. All sizes optional. */
export type BirdOptions = {
  /** Main plumage colour for the body. */
  bodyColor: number;
  /** Head colour (default: body colour). */
  headColor?: number;
  /** Folded-wing colour (default: a darker body shade). */
  wingColor?: number;
  /** Tail colour (default: wing colour). */
  tailColor?: number;
  /** Leg + foot colour (default: a dark amber). */
  legColor?: number;
  /** Beak colour (default: amber). */
  beakColor?: number;
  /** Body length along the facing axis (+z). Default 0.55. */
  length?: number;
  /** Body width across (x). Default 0.42. */
  width?: number;
  /** Body height (y). Default 0.4. */
  thickness?: number;
  /** Standing leg height (feet at y=0). Default 0.22. */
  legHeight?: number;
  /** Cube head size. Default 0.26. */
  headSize?: number;
  /** How high the head rides above the body top, as a fraction of headSize.
   *  Default 0.5 (a hen); ~1.2 reads as a goose/rooster neck. */
  neck?: number;
  /** How high the tail wedge rises above the body top. Default 0.12. */
  tailLift?: number;
};

/** Build the shared bird body into `p` (feet at y=0, facing +z) and return its
 *  anchor points for layering species features (comb, wattle, eyes, crest). */
export function bird(p: Prims, o: BirdOptions): Record<string, Vec3> {
  const { box, bone, ball, limb } = p;
  const len = o.length ?? 0.55;
  const wid = o.width ?? 0.42;
  const thick = o.thickness ?? 0.4;
  const legH = o.legHeight ?? 0.22;
  const headSz = o.headSize ?? 0.26;
  const neck = o.neck ?? 0.5;
  const tailLift = o.tailLift ?? 0.12;
  const headCol = o.headColor ?? o.bodyColor;
  const wingCol = o.wingColor ?? shade(o.bodyColor, -0.18);
  const tailCol = o.tailColor ?? wingCol;
  const legCol = o.legColor ?? 0x8a5a24;
  const beakCol = o.beakColor ?? 0xd9902a;

  const bodyY = legH + thick / 2;
  const bodyTop = legH + thick;
  const halfLen = len / 2;
  const legR = Math.max(0.025, wid * 0.07);

  // ---- Legs + feet ----
  // Pivot groups at the hip (top of the leg) so the gait animator can step them
  // (legL/legR, −x/+x side).
  const lx = wid * 0.2;
  for (const sx of [-1, 1]) {
    limb(sx < 0 ? "legL" : "legR", [sx * lx, legH, -len * 0.05], () => {
      bone(sx * lx, legH + thick * 0.18, -len * 0.05, sx * lx, legR * 1.2, -len * 0.05,
        legR * 1.05, legR * 0.85, legCol); // leg (top buried into the belly)
      // Foot: a flattened forward capsule with a rounded toe (depth squashes a
      // z-axis bone's vertical).
      bone(sx * lx, legR * 0.7, -len * 0.05 - legR * 1.4, sx * lx, legR * 0.7, -len * 0.05 + legR * 4.2,
        legR * 1.5, legR * 1.1, legCol, 0.5);
    });
  }

  // ---- Body: one egg — a tilted capsule, rump high, breast low + full ----
  bone(0, bodyY + thick * 0.08, -halfLen + wid * 0.2, 0, bodyY - thick * 0.1, halfLen * 0.5,
    wid * 0.46, wid * 0.5, o.bodyColor, (thick / wid) * 1.06);

  // ---- Folded wings on the flanks ----
  // Pivot at the wing's inner edge (against the body top) so the animator can
  // flare/flap it about the shoulder — wingL/wingR.
  const wingT = wid * 0.16;
  const wingX = wid / 2 + wingT / 2 - 0.01;
  for (const sx of [-1, 1]) {
    limb(sx < 0 ? "wingL" : "wingR", [sx * (wid / 2), bodyY + thick * 0.44, -len * 0.06], () => {
      // Shoulder→tip capsule hugging the flank, tapering toward the tail.
      bone(sx * (wid * 0.5), bodyY + thick * 0.3, len * 0.16, sx * (wid * 0.54), bodyY - thick * 0.02, -halfLen - len * 0.08,
        thick * 0.26, thick * 0.11, wingCol);
    });
  }

  // ---- Tail: one tapered capsule rising off the rump ----
  bone(0, bodyTop - thick * 0.3, -halfLen + len * 0.05, 0, bodyTop + tailLift + thick * 0.02, -halfLen - len * 0.26,
    thick * 0.28, thick * 0.12, tailCol);

  // ---- Neck + head + beak ----
  const headZ = halfLen - headSz * 0.1;
  const headY = bodyTop + headSz * neck + headSz / 2;
  bone(0, bodyTop - thick * 0.12, headZ - headSz * 0.05, 0, headY - headSz * 0.18, headZ - headSz * 0.05,
    headSz * 0.42, headSz * 0.36, headCol); // neck
  // Ball slightly larger than the old cube's inscribed sphere (×0.58) so
  // doc-layered face parts (comb/eyes at the anchors) still break the surface.
  ball(0, headY, headZ, headSz * 0.58, headCol); // head
  const beakLen = headSz * 0.55;
  box(0, headY - headSz * 0.08, headZ + headSz / 2 + beakLen / 2, headSz * 0.3, headSz * 0.24, beakLen, beakCol); // beak stays a hard wedge

  const points: Record<string, Vec3> = {
    root: [0, 0, 0],
    head: [0, headY, headZ],
    beak: [0, headY - headSz * 0.08, headZ + headSz / 2 + beakLen],
    crown: [0, headY + headSz / 2, headZ],
    back: [0, bodyTop, 0],
    breast: [0, bodyY - thick * 0.18, halfLen * 0.55 + len * 0.275],
    tail: [0, bodyTop + tailLift, -halfLen - len * 0.34],
    wingL: [-wingX - wingT / 2, bodyY + thick * 0.08, -len * 0.06],
    wingR: [wingX + wingT / 2, bodyY + thick * 0.08, -len * 0.06],
  };
  for (const name of ARCHETYPE_ANCHORS.bird) {
    if (!(name in points)) throw new Error(`bird anchor "${name}" missing`);
  }
  return points;
}
